import { defineStore } from 'pinia'
import { ref, reactive } from '#imports'
import { useRuntimeConfig } from '#app'
import { useAzureAdAuth } from '~/composables/useAzureAdAuth'

export type HubConnectionState = 'disconnected' | 'connecting' | 'connected' | 'reconnecting'

// Simple event bus (local) for decoupled listeners
type Handler = (payload: any) => void
const bus = {
  handlers: new Map<string, Set<Handler>>(),
  on(event: string, fn: Handler) {
    if (!this.handlers.has(event)) this.handlers.set(event, new Set())
    this.handlers.get(event)!.add(fn)
  },
  off(event: string, fn: Handler) {
    this.handlers.get(event)?.delete(fn)
  },
  emit(event: string, payload: any) {
    this.handlers.get(event)?.forEach(h => h(payload))
  }
}

export const useSignalRStore = defineStore('signalr', () => {
  const status = ref<HubConnectionState>('disconnected')
  const connection = ref<any | null>(null)

  // last received events + firehose
  const last = reactive({
    PickupPersonVerified: null as any,
    PickupRequestInitiated: null as any,
    StudentStatusChanged: null as any,
    PickupRequestApproved: null as any,
    ReunificationProcessUpdated: null as any,
    CustodianTransferRequested: null as any,
    CustodianTransferApprovedByAdmin: null as any,
    CustodianTransferCompleted: null as any,
    CustodianTransferCancelled: null as any,
    CustodianTransferExpired: null as any,
    ReceiveEvent: null as any,
  })
  const firehoseLog = ref<any[]>([])

  const subs = {
    students: new Set<string>(),
    custodianGroups: new Set<string>(),
    siteStaff: false,
    statusUpdates: false,
    processUpdates: false,
    all: false,
  }

  const joined = reactive({
    students: new Set<string>() as Set<string>,
    custodianGroups: new Set<string>() as Set<string>,
    siteStaff: false,
    statusUpdates: false,
    processUpdates: false,
    all: false,
  })

  const runtime = useRuntimeConfig()
  const { getToken } = useAzureAdAuth()

  function toQuery(query?: Record<string, string | number | boolean>) {
    if (!query) return ''
    const p = new URLSearchParams()
    for (const [k, v] of Object.entries(query)) if (v !== undefined && v !== null) p.set(k, String(v))
    const s = p.toString()
    return s ? `?${s}` : ''
  }

  async function loadSignalR() {
    // @ts-ignore
    const mod = await import(/* @vite-ignore */ '@microsoft/signalr').catch(() => null)
    if (!mod) throw new Error('Failed to load @microsoft/signalr')
    return mod as any
  }

  async function start(options: { query?: Record<string, string | number | boolean>, useAuthToken?: boolean } = {}) {
    if (connection.value || status.value === 'connecting') return
    status.value = 'connecting'
    const configured = runtime.public.apiBaseUrl?.replace(/\/$/, '') || ''
    const apiBase = configured || (typeof window !== 'undefined' ? window.location.origin : '')
    const hubUrl = apiBase + '/notificationHub' + toQuery(options.query)
    const token = options.useAuthToken === false ? null : await getToken().catch(() => null)
    const signalR = await loadSignalR()
    const builder = new signalR.HubConnectionBuilder()
      .withUrl(hubUrl, {
        accessTokenFactory: token ? () => token : undefined,
        transport: signalR.HttpTransportType.WebSockets | signalR.HttpTransportType.LongPolling,
      })
      .withAutomaticReconnect()
    const conn = builder.build()

    // Register handlers
    function setAndEmit(name: keyof typeof last, payload: any) {
      last[name] = payload
      bus.emit(String(name), payload)
    }
    conn.on('PickupPersonVerified', (d: any) => setAndEmit('PickupPersonVerified', d))
    conn.on('PickupRequestInitiated', (d: any) => setAndEmit('PickupRequestInitiated', d))
    conn.on('StudentStatusChanged', (d: any) => setAndEmit('StudentStatusChanged', d))
    conn.on('PickupRequestApproved', (d: any) => setAndEmit('PickupRequestApproved', d))
    conn.on('ReunificationProcessUpdated', (d: any) => setAndEmit('ReunificationProcessUpdated', d))
    // Custodian transfer events — delivered to redbag-{custodianGroupId} groups
    conn.on('CustodianTransferRequested', (d: any) => setAndEmit('CustodianTransferRequested', d))
    conn.on('CustodianTransferApprovedByAdmin', (d: any) => setAndEmit('CustodianTransferApprovedByAdmin', d))
    conn.on('CustodianTransferCompleted', (d: any) => setAndEmit('CustodianTransferCompleted', d))
    conn.on('CustodianTransferCancelled', (d: any) => setAndEmit('CustodianTransferCancelled', d))
    conn.on('CustodianTransferExpired', (d: any) => setAndEmit('CustodianTransferExpired', d))
    conn.on('ReceiveEvent', (envelope: any) => {
      setAndEmit('ReceiveEvent', envelope)
      firehoseLog.value.push({ ts: Date.now(), envelope })
      if (firehoseLog.value.length > 50) firehoseLog.value.shift()
    })

    conn.onreconnecting(() => { status.value = 'reconnecting' })
    conn.onreconnected(async () => { status.value = 'connected'; await rejoin() })
    conn.onclose(() => { status.value = 'disconnected'; connection.value = null })

    await conn.start()
    connection.value = conn
    status.value = 'connected'
    await rejoin()
  }

  async function ensureStarted() { if (!connection.value) await start() }

  async function subscribeStudent(studentId: string) {
    await ensureStarted(); if (!studentId) return
    try { await connection.value!.invoke('SubscribeToStudent', studentId) } catch { }
    subs.students.add(studentId); joined.students.add(studentId)
  }
  // Joins the redbag-{custodianGroupId} channel that carries the
  // CustodianTransfer* events. TODO(transfer-signalr): confirm the hub invoke
  // name once the hub side lands — untested, which is why transfer live
  // updates ship behind the enableTransferSignalR flag.
  async function subscribeCustodianGroup(custodianGroupId: string) {
    await ensureStarted(); if (!custodianGroupId) return
    try { await connection.value!.invoke('SubscribeToCustodianGroup', custodianGroupId) } catch { }
    subs.custodianGroups.add(custodianGroupId); joined.custodianGroups.add(custodianGroupId)
  }
  async function subscribeSiteStaff() {
    await ensureStarted(); if (!subs.siteStaff) { try { await connection.value!.invoke('SubscribeSiteStaff') } catch { } subs.siteStaff = true; joined.siteStaff = true }
  }
  async function subscribeStatusUpdates() {
    await ensureStarted(); if (!subs.statusUpdates) { try { await connection.value!.invoke('SubscribeStatusUpdates') } catch { } subs.statusUpdates = true; joined.statusUpdates = true }
  }
  async function subscribeProcessUpdates() {
    await ensureStarted(); if (!subs.processUpdates) { try { await connection.value!.invoke('SubscribeProcessUpdates') } catch { } subs.processUpdates = true; joined.processUpdates = true }
  }

  async function rejoin() {
    for (const s of subs.students) { try { await connection.value?.invoke('SubscribeToStudent', s) } catch { } }
    for (const g of subs.custodianGroups) { try { await connection.value?.invoke('SubscribeToCustodianGroup', g) } catch { } }
    if (subs.siteStaff) { try { await connection.value?.invoke('SubscribeSiteStaff') } catch { } }
    if (subs.statusUpdates) { try { await connection.value?.invoke('SubscribeStatusUpdates') } catch { } }
    if (subs.processUpdates) { try { await connection.value?.invoke('SubscribeProcessUpdates') } catch { } }
  }

  function clearFirehose() { firehoseLog.value = [] }

  // Quick, no-auth connect with query
  async function startQuick(opts?: { studentId?: string; role?: string; siteStaff?: boolean; statusUpdates?: boolean; processUpdates?: boolean; all?: boolean }) {
    await start({
      query: { studentId: opts?.studentId ?? '', role: opts?.role ?? '', all: opts?.all ? 'true' : '' },
      useAuthToken: false,
    })
    if (opts?.studentId) await subscribeStudent(opts.studentId)
    if (opts?.siteStaff || opts?.role === 'staff') await subscribeSiteStaff()
    if (opts?.statusUpdates) await subscribeStatusUpdates()
    if (opts?.processUpdates) await subscribeProcessUpdates()
    if (opts?.all) joined.all = true
  }

  return {
    // state
    status, last, firehoseLog, joined,
    // actions
    start, startQuick, ensureStarted, stop: async () => { if (connection.value) { await connection.value.stop(); connection.value = null; status.value = 'disconnected' } },
    subscribeStudent, subscribeCustodianGroup, subscribeSiteStaff, subscribeStatusUpdates, subscribeProcessUpdates,
    clearFirehose,
    // bus helpers for consumers
    on: bus.on.bind(bus), off: bus.off.bind(bus)
  }
})
